import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { StrictMode, Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Auth0Provider } from "./auth/Auth0Provider";
import { AuthProvider } from "./auth/AuthContext";
import { Toaster } from "sonner";

// Pre-auth pages - loaded immediately (small bundle)
import LoginPage from "./pages/authPages/LoginPage";
import AuthCallbackPage from "./pages/authPages/AuthCallbackPage";
import AuxiliaryAuthCallbackPage from "./pages/authPages/AuxiliaryAuthCallbackPage";
import PalantirAuthCallbackPage from "./pages/authPages/PalantirAuthCallbackPage";
import TenantSelectorPage from "./pages/authPages/TenantSelectorPage";
import AccessRequestLobbyPage from "./pages/authPages/AccessRequestLobbyPage";
import { VerifyEmailPage } from "./pages/authPages/VerifyEmailPage";
import { PasswordSetSuccessPage } from "./pages/authPages/PasswordSetSuccessPage";

// Post-auth app - lazy loaded (large bundle, loaded after authentication)
const AuthenticatedApp = lazy(() => import("./AuthenticatedApp"));

// Create QueryClient outside component to avoid recreation on every render
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30 * 1000,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
        },
    },
});

// Minimal loading component for authenticated app
function AuthAppLoader() {
    return (
        <div className="flex h-screen items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
    );
}

function App() {
    return (
        <StrictMode>
            <QueryClientProvider client={queryClient}>
                <Auth0Provider>
                    <AuthProvider>
                        <Router>
                            <Toaster />
                            <Routes>
                                {/* Pre-auth routes - in main bundle */}
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                                <Route
                                    path="/auth/auxiliary-callback"
                                    element={<AuxiliaryAuthCallbackPage />}
                                />
                                <Route path="/auth/palantir-callback" element={<PalantirAuthCallbackPage />} />
                                <Route path="/select-tenant" element={<TenantSelectorPage />} />
                                <Route path="/request-access" element={<AccessRequestLobbyPage />} />
                                <Route path="/verify-email" element={<VerifyEmailPage />} />
                                <Route
                                    path="/password-set-success"
                                    element={<PasswordSetSuccessPage />}
                                />
                                {/* Post-auth routes - lazy loaded */}
                                <Route
                                    path="/*"
                                    element={
                                        <Suspense fallback={<AuthAppLoader />}>
                                            <AuthenticatedApp />
                                        </Suspense>
                                    }
                                />
                            </Routes>
                        </Router>
                    </AuthProvider>
                </Auth0Provider>
            </QueryClientProvider>
        </StrictMode>
    );
}

export default App;
